import { useState, useEffect, useCallback, useRef } from "react";
import { login, logout, getAuth, AuthError } from "./auth";
import { loadAllData, saveKey, resetAllDataApi, downloadBackup, restoreBackup } from "./api";

// ── Constants ──────────────────────────────────────────────────────────────
const CONTRACT_HOURS_PER_DAY = 6;
const MILEAGE_RATE_LOW       = 0.45;
const MILEAGE_RATE_HIGH      = 0.25;
const MILEAGE_THRESHOLD      = 10000;
const HOLIDAY_ALLOWANCE      = 20;
const OPENING_BANK_BALANCE   = 28.25;
const OPENING_DATE           = "2026-04-10";

const UK_BANK_HOLIDAYS = [
  // England & Wales bank holidays from gov.uk/bank-holidays
  // 2025
  "2025-01-01", "2025-04-18", "2025-04-21", "2025-05-05",
  "2025-05-26", "2025-08-25", "2025-12-25", "2025-12-26",
  // 2026
  "2026-01-01", "2026-04-03", "2026-04-06", "2026-05-04",
  "2026-05-25", "2026-08-31", "2026-12-25", "2026-12-28",
  // 2027
  "2027-01-01", "2027-03-26", "2027-03-29", "2027-05-03",
  "2027-05-31", "2027-08-30", "2027-12-27", "2027-12-28",
  // 2028
  "2028-01-03", "2028-04-14", "2028-04-17", "2028-05-01",
  "2028-05-29", "2028-08-28", "2028-12-25", "2028-12-26",
];

// ── Helpers ────────────────────────────────────────────────────────────────
function getMondayOfWeek(dateStr) {
  const d = new Date(dateStr + "T12:00:00"), day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d.toISOString().slice(0, 10);
}
function getWeekDates(monday) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday + "T12:00:00"); d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });
}
function formatDate(dateStr) {
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short"
  });
}
function formatMonth(dateStr) {
  return new Date(dateStr + "-01T12:00:00").toLocaleDateString("en-GB", {
    month: "long", year: "numeric"
  });
}
function todayStr() { return new Date().toISOString().slice(0, 10); }

function computeHoursBank(entriesObj) {
  const delta = Object.entries(entriesObj).reduce((sum, [dateStr, e]) => {
    if (e.hours === "" || e.hours === undefined) return sum;
    const hours = parseFloat(e.hours);
    if (isNaN(hours)) return sum;
    // Count hours if it's a work day OR a weekend with hours entered (weekend work)
    if (e.type === "work") {
      return sum + (hours - CONTRACT_HOURS_PER_DAY);
    }
    if (e.type === "weekend") {
      // Weekend work: no contracted hours, all entered hours are pure surplus
      return sum + hours;
    }
    return sum;
  }, 0);
  return parseFloat((OPENING_BANK_BALANCE - delta).toFixed(2));
}

function getContractYearBounds() {
  const today = new Date(todayStr() + "T12:00:00");
  const thisStart = new Date(today.getFullYear() + "-04-10T12:00:00");
  if (today >= thisStart)
    return { start: today.getFullYear() + "-04-10", end: (today.getFullYear() + 1) + "-04-09" };
  return { start: (today.getFullYear() - 1) + "-04-10", end: today.getFullYear() + "-04-09" };
}

// ══ App ════════════════════════════════════════════════════════════════════
export default function App() {
  const [role, setRole]       = useState(null);
  const [dataLoading, setDataLoading] = useState(false);

  const [entries, setEntries]         = useState({});
  const [hoursBank, setHoursBank]     = useState(OPENING_BANK_BALANCE);
  const [bankLog, setBankLog]         = useState([]);
  const [milagePaid, setMilagePaid]   = useState({});
  const [annualMiles, setAnnualMiles] = useState(0);
  const [saving, setSaving]           = useState(false);
  const saveTimers = useRef({});

  const [currentWeekMonday, setCurrentWeekMonday] = useState(() => getMondayOfWeek(todayStr()));
  const [view, setView]                   = useState("week");
  const [bankAdjAmount, setBankAdjAmount] = useState("");
  const [bankAdjNote, setBankAdjNote]     = useState("");
  const [confirmReset, setConfirmReset]   = useState(false);
  const [toast, setToast]                 = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 3000);
  };

  // Called on any 401 from the API — clears session and forces a full reload
  // for a clean state (rather than trying to reset dozens of React state vars).
  const handleAuthError = useCallback(() => {
    logout();
    window.location.reload();
  }, []);

  const applyData = useCallback((data) => {
    setEntries(data.entries);
    setHoursBank(computeHoursBank(data.entries));
    setBankLog(data.bankLog.length ? data.bankLog : [{
      date: OPENING_DATE, delta: OPENING_BANK_BALANCE,
      note: "Opening balance", balance: OPENING_BANK_BALANCE
    }]);
    setMilagePaid(data.milagePaid);
    setAnnualMiles(data.annualMiles);
  }, []);

  // ── Bootstrap: if session token exists, try to load data immediately ──────
  useEffect(() => {
    const auth = getAuth();
    if (!auth) return;
    setDataLoading(true);
    loadAllData()
      .then(data => { applyData(data); setRole(auth.role); })
      .catch(err => {
        if (err instanceof AuthError) handleAuthError();
      })
      .finally(() => setDataLoading(false));
  }, [applyData, handleAuthError]);

  // ── Login handler ─────────────────────────────────────────────────────────
  const handleLoginSuccess = useCallback(async (r) => {
    setDataLoading(true);
    try {
      const data = await loadAllData();
      applyData(data);
      setRole(r);
    } catch (err) {
      if (err instanceof AuthError) handleAuthError();
      else showToast("Failed to load data", "error");
    } finally {
      setDataLoading(false);
    }
  }, [applyData, handleAuthError]);

  // ── Entry update ──────────────────────────────────────────────────────────
  const updateEntry = useCallback((dateStr, field, value) => {
    if (dateStr < "2026-04-01") return;
    setEntries(prev => {
      const existing   = prev[dateStr] || { hours: "", miles: "", type: "work" };
      const updated    = { ...existing, [field]: value };
      const newEntries = { ...prev, [dateStr]: updated };

      if (field === "hours" || field === "type") {
        const newBank = computeHoursBank(newEntries);
        setHoursBank(newBank);
        if (field === "hours" && value !== "" && value !== existing.hours) {
          setBankLog(bl => [...bl, {
            date: todayStr(),
            delta: parseFloat(((parseFloat(value)||0) - (parseFloat(existing.hours)||0)).toFixed(2)),
            note: `${formatDate(dateStr)}: ${value}h logged`,
            balance: newBank,
          }]);
        }
      }
      if (field === "miles") {
        const oldM = parseFloat(existing.miles) || 0, newM = parseFloat(value) || 0;
        setAnnualMiles(am => parseFloat((am + newM - oldM).toFixed(1)));
      }

      setSaving(true);
      if (saveTimers.current[dateStr]) clearTimeout(saveTimers.current[dateStr]);
      saveTimers.current[dateStr] = setTimeout(() => {
        saveKey("entries", newEntries)
          .then(() => setSaving(false))
          .catch(err => {
            setSaving(false);
            if (err instanceof AuthError) handleAuthError();
            else showToast("Save failed — check connection", "error");
          });
      }, 1500);

      return newEntries;
    });
  }, [handleAuthError]);

  // ── Admin actions ─────────────────────────────────────────────────────────
  const applyBankAdjustment = async () => {
    const delta = parseFloat(bankAdjAmount); if (isNaN(delta)) return;
    const nb = parseFloat((hoursBank + delta).toFixed(2));
    const newLog = [...bankLog, { date: todayStr(), delta, note: bankAdjNote || "Admin adjustment", balance: nb }];
    setHoursBank(nb); setBankLog(newLog); setBankAdjAmount(""); setBankAdjNote("");
    setSaving(true);
    try { await saveKey("bankLog", newLog); showToast("Hours bank updated"); }
    catch (err) { if (err instanceof AuthError) handleAuthError(); else showToast("Save failed", "error"); }
    finally { setSaving(false); }
  };

  const zeroBankWithPayment = async () => {
    const newLog = [...bankLog, { date: todayStr(), delta: -hoursBank, note: "One-off payment issued — bank zeroed", balance: 0 }];
    setHoursBank(0); setBankLog(newLog); setSaving(true);
    try { await saveKey("bankLog", newLog); showToast("Bank zeroed — payment logged"); }
    catch (err) { if (err instanceof AuthError) handleAuthError(); else showToast("Save failed", "error"); }
    finally { setSaving(false); }
  };

  const resetAllData = async () => {
    const initialLog = [{ date: OPENING_DATE, delta: OPENING_BANK_BALANCE, note: "Opening balance", balance: OPENING_BANK_BALANCE }];
    setEntries({}); setHoursBank(OPENING_BANK_BALANCE); setBankLog(initialLog);
    setMilagePaid({}); setAnnualMiles(0);
    setCurrentWeekMonday(getMondayOfWeek(OPENING_DATE)); setConfirmReset(false); setSaving(true);
    try { await resetAllDataApi(initialLog); showToast("All data reset"); }
    catch (err) { if (err instanceof AuthError) handleAuthError(); else showToast("Reset failed", "error"); }
    finally { setSaving(false); }
  };

  const handleBackup = async () => {
    try {
      const backup = await downloadBackup();
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hours-tracker-backup-${todayStr()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("Backup downloaded ✓");
    } catch (err) {
      if (err instanceof AuthError) handleAuthError();
      else showToast("Backup failed", "error");
    }
  };

  const handleRestore = async (file) => {
    try {
      const text = await file.text();
      const backup = JSON.parse(text);
      if (!backup?.data || backup.version !== 1) {
        showToast("Invalid backup file", "error");
        return;
      }
      setSaving(true);
      await restoreBackup(backup);
      // Reload all data from server after restore
      const data = await loadAllData();
      applyData(data);
      showToast("Data restored ✓");
    } catch (err) {
      if (err instanceof AuthError) handleAuthError();
      else showToast("Restore failed — check file is valid", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleMilagePaid = async (monthKey) => {
    const updated = { ...milagePaid, [monthKey]: !milagePaid[monthKey] };
    setMilagePaid(updated); setSaving(true);
    try { await saveKey("milagePaid", updated); showToast(updated[monthKey] ? "Marked as paid ✓" : "Marked as unpaid"); }
    catch (err) { if (err instanceof AuthError) handleAuthError(); else showToast("Save failed", "error"); }
    finally { setSaving(false); }
  };

  // ── Calculations ──────────────────────────────────────────────────────────
  const getMonthMiles = (monthKey) =>
    Object.entries(entries).filter(([d, e]) => d.startsWith(monthKey) && (e.type === "work" || e.type === "weekend"))
      .reduce((s, [, e]) => s + (parseFloat(e.miles) || 0), 0);

  const getMonthMileagePay = (monthKey) => {
    const allMonths = [...new Set(Object.keys(entries).map(d => d.slice(0,7)))].sort();
    let cumMiles = 0, pay = 0;
    for (const m of allMonths) {
      const mMiles = getMonthMiles(m), startCum = cumMiles; cumMiles += mMiles;
      if (m === monthKey) {
        const lo = Math.min(mMiles, Math.max(0, MILEAGE_THRESHOLD - startCum));
        pay = lo * MILEAGE_RATE_LOW + (mMiles - lo) * MILEAGE_RATE_HIGH; break;
      }
    }
    return pay;
  };

  const { start: cyStart, end: cyEnd } = getContractYearBounds();
  const today = todayStr();
  const inCY   = d => d >= cyStart && d <= cyEnd;
  const isPast = d => d <= today;
  // Holidays: distinguish between already taken (past) vs scheduled (future)
  const holidayEntries        = Object.entries(entries).filter(([d,e]) => e.type==="holiday"     && inCY(d));
  const holidaysTaken         = holidayEntries.filter(([d]) => isPast(d)).length;
  const holidaysScheduled     = holidayEntries.filter(([d]) => !isPast(d)).length;
  const holidayDays           = holidaysTaken + holidaysScheduled; // total booked
  const sickDays              = Object.entries(entries).filter(([d,e]) => e.type==="sick"        && inCY(d)).length;
  const bankHolEntries        = Object.entries(entries).filter(([d,e]) => e.type==="bankholiday" && inCY(d));
  const bankHolsTaken         = bankHolEntries.filter(([d]) => isPast(d)).length;
  const bankHolsScheduled     = bankHolEntries.filter(([d]) => !isPast(d)).length;
  const bankHolidaysUsed      = bankHolsTaken + bankHolsScheduled;
  // Count UK bank holidays that fall on weekdays (Mon-Fri) within this contract year.
  // These are added to the base 20-day allowance to get total paid leave.
  const bankHolsInCY = UK_BANK_HOLIDAYS.filter(d => {
    if (!inCY(d)) return false;
    const dow = new Date(d + "T12:00:00").getDay();
    return dow >= 1 && dow <= 5; // Mon-Fri only
  }).length;
  const totalAllowance = HOLIDAY_ALLOWANCE + bankHolsInCY;
  const leaveBooked    = holidayDays + bankHolidaysUsed;       // all committed leave (past + future)
  const leaveTaken     = holidaysTaken + bankHolsTaken;         // actually used already
  const leaveRemaining = totalAllowance - leaveBooked;         // what's still available to book
  const weekDates = getWeekDates(currentWeekMonday);
  const allMonths = [...new Set(Object.keys(entries).map(d => d.slice(0,7)))].sort().reverse();
  // Miles driven this calendar month (for header display)
  const currentMonthKey = todayStr().slice(0, 7);
  const milesThisMonth  = Object.entries(entries)
    .filter(([d, e]) => d.startsWith(currentMonthKey) && (e.type === "work" || e.type === "weekend"))
    .reduce((s, [, e]) => s + (parseFloat(e.miles) || 0), 0);

  // ── Render ────────────────────────────────────────────────────────────────
  if (dataLoading) return <Splash text="Loading…" />;

  if (!role) return (
    <LoginScreen onSuccess={handleLoginSuccess} />
  );

  return (
    <div style={S.app}>
      <header style={S.header}>
        <div style={S.headerLeft}>
          <span style={S.logo}>◈</span>
          <div><div style={S.headerTitle}>Hours Tracker</div><div style={S.headerSub}>{role === "admin" ? "Admin view: " : ""}Sasha · Hours &amp; Mileage</div></div>
        </div>
        <div style={S.headerRight}>
          {saving && <span style={S.savingDot} title="Saving…">●</span>}
          <div style={S.headerStat}>
            <span style={{...S.headerStatVal, color:"#c9a84c"}}>
              {milesThisMonth.toFixed(0)}
            </span>
            <span style={S.headerStatLbl}>miles this month</span>
          </div>
          <div style={S.headerDivider}/>
          <div style={S.headerStat}>
            <span style={{...S.headerStatVal, color: hoursBank<0?"#e05c5c":hoursBank===0?"#888":"#5cb85c"}}>
              {hoursBank>0?"+":""}{hoursBank.toFixed(2)}h
            </span>
            <span style={S.headerStatLbl}>hours bank</span>
          </div>
          <div style={S.headerDivider}/>
          <div style={S.headerStat}>
            <span style={{...S.headerStatVal, color:leaveRemaining<3?"#e05c5c":"#c9a84c"}}>
              {leaveRemaining}
            </span>
            <span style={S.headerStatLbl}>leave left</span>
          </div>
          <button style={S.logoutBtn} onClick={() => { logout(); window.location.reload(); }}>Sign out</button>
        </div>
      </header>

      <nav style={S.nav}>
        {[["week","Week"],["monthly","Mileage"],["bank","Hours Bank"],["holidays","Leave"],["summary","Summary"],["export","Export"]].map(([v,label]) => (
          <button key={v} style={{...S.navBtn,...(view===v?S.navBtnActive:{})}} onClick={() => setView(v)}>{label}</button>
        ))}
      </nav>

      <main style={S.main}>
        {view==="week"     && <WeekView dates={weekDates} monday={currentWeekMonday} entries={entries} role={role} updateEntry={updateEntry} onPrev={() => { const d=new Date(currentWeekMonday+"T12:00:00"); d.setDate(d.getDate()-7); setCurrentWeekMonday(d.toISOString().slice(0,10)); }} onNext={() => { const d=new Date(currentWeekMonday+"T12:00:00"); d.setDate(d.getDate()+7); setCurrentWeekMonday(d.toISOString().slice(0,10)); }} onToday={() => setCurrentWeekMonday(getMondayOfWeek(todayStr()))} />}
        {view==="monthly"  && <MileageView allMonths={allMonths} getMonthMiles={getMonthMiles} getMonthMileagePay={getMonthMileagePay} milagePaid={milagePaid} toggleMilagePaid={toggleMilagePaid} role={role} annualMiles={annualMiles} />}
        {view==="bank"     && <BankView hoursBank={hoursBank} bankLog={bankLog} role={role} bankAdjAmount={bankAdjAmount} setBankAdjAmount={setBankAdjAmount} bankAdjNote={bankAdjNote} setBankAdjNote={setBankAdjNote} applyBankAdjustment={applyBankAdjustment} zeroBankWithPayment={zeroBankWithPayment} confirmReset={confirmReset} setConfirmReset={setConfirmReset} resetAllData={resetAllData} handleBackup={handleBackup} handleRestore={handleRestore} />}
        {view==="holidays" && <HolidayView holidaysTaken={holidaysTaken} holidaysScheduled={holidaysScheduled} bankHolsTaken={bankHolsTaken} bankHolsScheduled={bankHolsScheduled} sickDays={sickDays} bankHolsInCY={bankHolsInCY} totalAllowance={totalAllowance} leaveRemaining={leaveRemaining} leaveTaken={leaveTaken} entries={entries} cyStart={cyStart} cyEnd={cyEnd} />}
        {view==="summary"  && <SummaryView entries={entries} milagePaid={milagePaid} getMonthMileagePay={getMonthMileagePay} />}
        {view==="export"   && <ExportView entries={entries} getMonthMileagePay={getMonthMileagePay} />}
      </main>

      {toast && <div style={{...S.toast, background:toast.type==="error"?"#2a1515":"#1e2a20", borderColor:toast.type==="error"?"#e05c5c":"#5cb85c", color:toast.type==="error"?"#e05c5c":"#5cb85c"}}>{toast.msg}</div>}
    </div>
  );
}

// ── Splash ────────────────────────────────────────────────────────────────
function Splash({ text }) {
  return (
    <div style={S.loginWrap}>
      <div style={{textAlign:"center", color:"#778", fontSize:14}}>
        <div style={S.spinner}/>
        <p style={{marginTop:16}}>{text}</p>
      </div>
    </div>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────
function LoginScreen({ onSuccess }) {
  const [pw, setPw]       = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!pw.trim() || loading) return;
    setLoading(true); setError("");
    const result = await login(pw);
    setPw("");
    if (result.ok) {
      onSuccess(result.role);
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div style={S.loginWrap}>
      <div style={S.loginCard}>
        <div style={S.loginLogo}>◈</div>
        <h1 style={S.loginTitle}>Hours Tracker</h1>
        <p style={S.loginSub}>Enter your access password</p>
        <input
          style={{...S.input, ...S.loginInput, ...(error ? S.inputError : {})}}
          type="password" placeholder="Password" value={pw}
          onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          autoFocus disabled={loading}
        />
        {error && <p style={S.errorMsg}>{error}</p>}
        <button style={{...S.loginBtn, opacity: loading ? 0.7 : 1}}
          onClick={handleSubmit} disabled={loading}>
          {loading ? "Connecting…" : "Enter"}
        </button>
      </div>
    </div>
  );
}

// ── Week View ─────────────────────────────────────────────────────────────
function WeekView({ dates, monday, entries, role, updateEntry, onPrev, onNext, onToday }) {
  const weekStart = new Date(monday+"T12:00:00");
  const weekEnd   = new Date(dates[dates.length - 1]+"T12:00:00");
  const label = `${weekStart.toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – ${weekEnd.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}`;
  return (
    <section>
      <div style={S.weekNav}>
        <button style={S.iconBtn} onClick={onPrev}>‹</button>
        <div style={S.weekLabel}><span>{label}</span><button style={S.todayBtn} onClick={onToday}>Today</button></div>
        <button style={S.iconBtn} onClick={onNext}>›</button>
      </div>
      <div style={S.dayGrid}>
        {dates.map(d => <DayCard key={d} dateStr={d} entry={entries[d]||{hours:"",miles:"",type:"work"}} role={role} updateEntry={updateEntry}/>)}
      </div>
    </section>
  );
}

// ── Day Card ──────────────────────────────────────────────────────────────
function DayCard({ dateStr, entry, role, updateEntry }) {
  const isToday   = dateStr === todayStr();
  const isBankHol = UK_BANK_HOLIDAYS.includes(dateStr);
  const dow       = new Date(dateStr + "T12:00:00").getDay();
  const isWeekend = dow === 0 || dow === 6; // Sun or Sat
  const rawType   = entry.type;
  // Auto-default: bank holiday > weekend > work
  useEffect(() => {
    if (rawType) return;
    if (isBankHol)      updateEntry(dateStr, "type", "bankholiday");
    else if (isWeekend) updateEntry(dateStr, "type", "weekend");
  }, [dateStr, isBankHol, isWeekend, rawType, updateEntry]);
  const type = entry.type || (isBankHol ? "bankholiday" : isWeekend ? "weekend" : "work");
  const tc = {
    work:        { bg:"#1a1f2e", border:"#2d3550" },
    holiday:     { bg:"#1a2820", border:"#3a6040" },
    sick:        { bg:"#2a1f1f", border:"#5c3030" },
    bankholiday: { bg:"#1e1a2a", border:"#4a3560" },
    weekend:     { bg:"#141820", border:"#242a3a" },
  }[type] || { bg:"#1a1f2e", border:"#2d3550" };
  const isLocked = dateStr < "2026-04-01";
  const canEdit  = !isLocked && (role === "admin" || role === "user");

  // Hours/miles shown for: normal work days AND weekends (so Sasha can log weekend work
  // without switching day type). Weekends default to 0 contracted hours, so anything
  // entered counts as pure surplus.
  const showHoursAndMiles = type === "work" || type === "weekend";

  return (
    <div style={{...S.dayCard, background:tc.bg, borderColor:isToday?"#c9a84c":tc.border, opacity:isLocked?0.45:1, pointerEvents:isLocked?"none":"auto"}}>
      <div style={S.dayCardHeader}>
        <span style={{...S.dayName, color:isToday?"#c9a84c":"#aab"}}>{formatDate(dateStr)}</span>
        {isBankHol && type !== "bankholiday" && <span style={S.bhBadge}>BH</span>}
        {type === "weekend" && <span style={S.weBadge}>WE</span>}
      </div>
      {canEdit && !isWeekend && (
        <select style={S.typeSelect} value={type} onChange={e => updateEntry(dateStr, "type", e.target.value)}>
          <option value="work">Work day</option>
          <option value="holiday">Holiday</option>
          <option value="sick">Sick day</option>
          <option value="bankholiday">Bank Holiday</option>
        </select>
      )}
      {showHoursAndMiles && (
        <div style={S.dayFields}>
          <label style={S.fieldLabel}>
            {type === "weekend" ? "Hours worked (optional)" : "Hours worked"}
          </label>
          <input style={S.input} type="number" min="0" max="24" step="0.25" value={entry.hours || ""} readOnly={!canEdit} onChange={e => updateEntry(dateStr, "hours", e.target.value)} />
          <label style={S.fieldLabel}>
            {type === "weekend" ? "Miles driven (optional)" : "Miles driven"}
          </label>
          <input style={S.input} type="number" min="0" step="0.1" value={entry.miles || ""} readOnly={!canEdit} onChange={e => updateEntry(dateStr, "miles", e.target.value)} />
        </div>
      )}
      {!showHoursAndMiles && (
        <div style={S.dayTypeLabel}>
          {type==="holiday"     ? "🌴 Holiday taken"
          : type==="sick"       ? "🤒 Sick day"
          : type==="bankholiday"? "🏦 Bank Holiday"
          : null}
        </div>
      )}
    </div>
  );
}

// ── Mileage View ──────────────────────────────────────────────────────────
function MileageView({ allMonths, getMonthMiles, getMonthMileagePay, milagePaid, toggleMilagePaid, role, annualMiles }) {
  const rateInfo = annualMiles <= MILEAGE_THRESHOLD
    ? `${(MILEAGE_THRESHOLD - annualMiles).toFixed(0)} mi remaining at 45p`
    : `All miles now at 25p`;
  return (
    <section>
      <div style={S.sectionHeader}>
        <h2 style={S.sectionTitle}>Monthly Mileage Payments</h2>
        <div style={S.infoChip}>{annualMiles.toFixed(0)} annual miles · {rateInfo}</div>
      </div>
      {allMonths.length === 0 && <Empty text="No mileage data yet." />}
      <div style={S.monthList}>
        {allMonths.map(m => {
          const miles = getMonthMiles(m), pay = getMonthMileagePay(m), paid = !!milagePaid[m];
          return (
            <div key={m} style={{...S.monthRow, opacity:paid?0.65:1}}>
              <div>
                <div style={S.monthName}>{formatMonth(m)}</div>
                <div style={S.monthDetail}>{miles.toFixed(1)} miles · <strong>£{pay.toFixed(2)}</strong></div>
              </div>
              {role === "admin" && (
                <button style={{...S.paidToggle, background:paid?"#2a4a2a":"#1a1f2e", borderColor:paid?"#5cb85c":"#444"}}
                  onClick={() => toggleMilagePaid(m)}>
                  {paid ? "✓ Paid" : "Unpaid"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Bank View ─────────────────────────────────────────────────────────────
function BankView({ hoursBank, bankLog, role, bankAdjAmount, setBankAdjAmount, bankAdjNote, setBankAdjNote, applyBankAdjustment, zeroBankWithPayment, confirmReset, setConfirmReset, resetAllData, handleBackup, handleRestore }) {
  const neg = hoursBank < 0;
  const fileInputRef = useRef(null);
  const [confirmRestore, setConfirmRestore] = useState(null); // holds pending File
  return (
    <section>
      <div style={S.sectionHeader}><h2 style={S.sectionTitle}>Hours Bank</h2></div>
      <div style={S.bankHero}>
        <div style={{...S.bankBig, color:neg?"#e05c5c":hoursBank===0?"#888":"#5cb85c"}}>
          {hoursBank > 0 ? "+" : ""}{hoursBank.toFixed(2)}h
        </div>
        <div style={S.bankHeroSub}>{neg?"Sasha is owed hours":hoursBank===0?"Bank is balanced":"Hours in credit"}</div>
      </div>
      {role === "admin" && (
        <div style={S.adminPanel}>
          <h3 style={S.adminPanelTitle}>Admin Adjustment</h3>
          <div style={S.adjRow}>
            <input style={{...S.input,flex:1}} type="number" step="0.25" placeholder="e.g. +2 or -3.5 hours" value={bankAdjAmount} onChange={e => setBankAdjAmount(e.target.value)} />
            <input style={{...S.input,flex:2}} type="text" placeholder="Note (optional)" value={bankAdjNote} onChange={e => setBankAdjNote(e.target.value)} />
            <button style={S.adjBtn} onClick={applyBankAdjustment}>Apply</button>
          </div>
          {hoursBank < 0 && <button style={S.zeroBtn} onClick={zeroBankWithPayment}>Issue one-off payment &amp; zero bank</button>}

          <div style={S.backupZone}>
            <h4 style={S.backupTitle}>Backup &amp; Restore</h4>
            <p style={S.backupHint}>Download a full backup monthly. Save it to iCloud or email it to yourself.</p>
            <div style={S.backupRow}>
              <button style={S.backupBtn} onClick={handleBackup}>↓ Download backup</button>
              <button style={S.backupBtn} onClick={() => fileInputRef.current?.click()}>↑ Restore from file</button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                style={{display:"none"}}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) setConfirmRestore(f);
                  e.target.value = ""; // reset so same file can be picked again
                }}
              />
            </div>
            {confirmRestore && (
              <div style={S.resetConfirm}>
                <span style={S.resetConfirmText}>
                  Restore from <strong>{confirmRestore.name}</strong>? This will overwrite ALL current data.
                </span>
                <div style={S.resetConfirmBtns}>
                  <button style={S.resetConfirmYes} onClick={() => { handleRestore(confirmRestore); setConfirmRestore(null); }}>Yes, restore</button>
                  <button style={S.resetConfirmNo} onClick={() => setConfirmRestore(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div style={S.resetZone}>
            {!confirmReset
              ? <button style={S.resetBtn} onClick={() => setConfirmReset(true)}>⚠ Reset all app data</button>
              : <div style={S.resetConfirm}>
                  <span style={S.resetConfirmText}>This will erase all entries. Are you sure?</span>
                  <div style={S.resetConfirmBtns}>
                    <button style={S.resetConfirmYes} onClick={resetAllData}>Yes, reset everything</button>
                    <button style={S.resetConfirmNo} onClick={() => setConfirmReset(false)}>Cancel</button>
                  </div>
                </div>
            }
          </div>
        </div>
      )}
      <h3 style={S.logTitle}>Bank history</h3>
      {bankLog.length === 0 && <Empty text="No entries yet." />}
      <div style={S.logList}>
        {[...bankLog].reverse().map((e, i) => (
          <div key={i} style={S.logRow}>
            <span style={S.logDate}>{formatDate(e.date)}</span>
            <span style={S.logNote}>{e.note}</span>
            <span style={{...S.logDelta, color:e.delta>=0?"#5cb85c":"#e05c5c"}}>{e.delta>0?"+":""}{e.delta.toFixed(2)}h</span>
            <span style={S.logBalance}>{e.balance>0?"+":""}{e.balance.toFixed(2)}h</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Holiday View ──────────────────────────────────────────────────────────
function HolidayView({ holidaysTaken, holidaysScheduled, bankHolsTaken, bankHolsScheduled, sickDays, bankHolsInCY, totalAllowance, leaveRemaining, leaveTaken, entries, cyStart, cyEnd }) {
  const today = todayStr();
  const inCY  = d => d >= cyStart && d <= cyEnd;
  const holsPastEntries     = Object.entries(entries).filter(([d,e]) => e.type==="holiday"     && inCY(d) && d<=today).sort(([a],[b]) => b.localeCompare(a));
  const holsFutureEntries   = Object.entries(entries).filter(([d,e]) => e.type==="holiday"     && inCY(d) && d> today).sort(([a],[b]) => a.localeCompare(b));
  const bankHolsPastEntries   = Object.entries(entries).filter(([d,e]) => e.type==="bankholiday" && inCY(d) && d<=today).sort(([a],[b]) => b.localeCompare(a));
  const bankHolsFutureEntries = Object.entries(entries).filter(([d,e]) => e.type==="bankholiday" && inCY(d) && d> today).sort(([a],[b]) => a.localeCompare(b));
  const sickEntries           = Object.entries(entries).filter(([d,e]) => e.type==="sick"        && inCY(d)).sort(([a],[b]) => b.localeCompare(a));
  const totalHolidays  = holidaysTaken + holidaysScheduled;
  const leaveBooked    = totalHolidays + bankHolsTaken + bankHolsScheduled;
  const progress       = totalAllowance > 0 ? (leaveBooked / totalAllowance) * 100 : 0;
  return (
    <section>
      <div style={S.sectionHeader}>
        <h2 style={S.sectionTitle}>Leave &amp; Attendance</h2>
        <div style={S.infoChip}>Contract year: {cyStart} – {cyEnd} · Total allowance: {totalAllowance} days ({HOLIDAY_ALLOWANCE} holiday + {bankHolsInCY} bank holidays)</div>
      </div>
      <div style={S.leaveCards}>
        <div style={S.leaveCard}>
          <div style={S.leaveNum}>{holidaysTaken}</div>
          <div style={S.leaveLbl}>Holidays taken</div>
          <div style={S.leaveOut}>{holidaysScheduled > 0 ? `+ ${holidaysScheduled} scheduled` : "nothing scheduled"}</div>
        </div>
        <div style={S.leaveCard}>
          <div style={S.leaveNum}>{bankHolsTaken}</div>
          <div style={S.leaveLbl}>Bank hols taken</div>
          <div style={S.leaveOut}>{bankHolsScheduled > 0 ? `+ ${bankHolsScheduled} scheduled` : "nothing scheduled"}</div>
        </div>
        <div style={S.leaveCard}>
          <div style={S.leaveNum}>{leaveRemaining}</div>
          <div style={S.leaveLbl}>Leave remaining</div>
          <div style={S.leaveOut}>available to book</div>
        </div>
        <div style={S.leaveCard}>
          <div style={S.leaveNum}>{sickDays}</div>
          <div style={S.leaveLbl}>Sick days</div>
          <div style={S.leaveOut}>this contract year</div>
        </div>
      </div>
      <div style={S.barWrap}><div style={{...S.bar, width:`${Math.min(100, progress)}%`, background:leaveRemaining<3?"#e05c5c":"#c9a84c"}}/></div>

      {holsPastEntries.length > 0 && (
        <>
          <h3 style={S.logTitle}>Holidays taken</h3>
          <div style={S.logList}>{holsPastEntries.map(([d]) => <div key={d} style={S.logRow}><span style={S.logDate}>{formatDate(d)}</span><span style={{...S.logDelta,color:"#5cb85c"}}>🌴 Holiday</span></div>)}</div>
        </>
      )}
      {holsFutureEntries.length > 0 && (
        <>
          <h3 style={S.logTitle}>Holidays scheduled</h3>
          <div style={S.logList}>{holsFutureEntries.map(([d]) => <div key={d} style={S.logRow}><span style={S.logDate}>{formatDate(d)}</span><span style={{...S.logDelta,color:"#c9a84c"}}>📅 Holiday</span></div>)}</div>
        </>
      )}
      {bankHolsPastEntries.length > 0 && (
        <>
          <h3 style={S.logTitle}>Bank holidays taken</h3>
          <div style={S.logList}>{bankHolsPastEntries.map(([d]) => <div key={d} style={S.logRow}><span style={S.logDate}>{formatDate(d)}</span><span style={{...S.logDelta,color:"#cba8ff"}}>🏦 Bank Hol</span></div>)}</div>
        </>
      )}
      {bankHolsFutureEntries.length > 0 && (
        <>
          <h3 style={S.logTitle}>Bank holidays scheduled</h3>
          <div style={S.logList}>{bankHolsFutureEntries.map(([d]) => <div key={d} style={S.logRow}><span style={S.logDate}>{formatDate(d)}</span><span style={{...S.logDelta,color:"#cba8ff"}}>🏦 Bank Hol</span></div>)}</div>
        </>
      )}
      {sickEntries.length > 0 && (
        <>
          <h3 style={S.logTitle}>Sick days</h3>
          <div style={S.logList}>{sickEntries.map(([d]) => <div key={d} style={S.logRow}><span style={S.logDate}>{formatDate(d)}</span><span style={{...S.logDelta,color:"#e05c5c"}}>🤒 Sick</span></div>)}</div>
        </>
      )}
    </section>
  );
}

// ── Summary View ──────────────────────────────────────────────────────────
function SummaryView({ entries, milagePaid, getMonthMileagePay }) {
  const years = [{ start:"2026-04-10", end:"2027-04-09", label:"Apr 2026 – Apr 2027" }];
  return (
    <section>
      <div style={S.sectionHeader}>
        <h2 style={S.sectionTitle}>Year Summaries</h2>
        <div style={S.infoChip}>Contract year runs 10 Apr – 9 Apr</div>
      </div>
      {years.map(({ start, end, label }) => {
        const inY = d => d >= start && d <= end;
        const ye  = Object.entries(entries).filter(([d]) => inY(d));
        const workDays    = ye.filter(([,e]) => e.type==="work" && e.hours!=="").length;
        const weekendDaysWorked = ye.filter(([,e]) => e.type==="weekend" && e.hours!=="" && parseFloat(e.hours) > 0).length;
        const weekdayHours   = ye.filter(([,e]) => e.type==="work" && e.hours!=="").reduce((s,[,e]) => s+parseFloat(e.hours), 0);
        const weekendHours   = ye.filter(([,e]) => e.type==="weekend" && e.hours!=="").reduce((s,[,e]) => s+parseFloat(e.hours), 0);
        const totalHours  = weekdayHours + weekendHours;
        const totalMiles  = ye.filter(([,e]) => e.type==="work" || e.type==="weekend").reduce((s,[,e]) => s+(parseFloat(e.miles)||0), 0);
        // Bank delta: weekdays contribute (hours - 6), weekends contribute full hours
        const bankDelta   = (weekdayHours - workDays * CONTRACT_HOURS_PER_DAY) + weekendHours;
        const yearMonths  = [...new Set(ye.map(([d]) => d.slice(0,7)))].sort();
        const totalMilPay = yearMonths.reduce((s,m) => s+getMonthMileagePay(m), 0);
        const paidMonths  = yearMonths.filter(m => milagePaid[m]).length;
        const holDays     = ye.filter(([,e]) => e.type==="holiday").length;
        const sickDays    = ye.filter(([,e]) => e.type==="sick").length;
        return (
          <div key={start} style={S.yearCard}>
            <div style={S.yearCardHeader}>
              <div style={S.yearCardTitle}>{label}</div>
              {end > todayStr() && <span style={S.currentBadge}>Current year</span>}
            </div>
            <div style={S.yearStats}>
              {[
                [totalHours.toFixed(2)+"h", "Hours worked"],
                [(bankDelta>=0?"+":"")+bankDelta.toFixed(2)+"h", "Over contract", bankDelta>0?"#e05c5c":"#5cb85c"],
                [totalMiles.toFixed(1), "Miles driven"],
                ["£"+totalMilPay.toFixed(2), `Mileage pay (${paidMonths}/${yearMonths.length} paid)`],
                [holDays+" / "+HOLIDAY_ALLOWANCE, "Holidays"],
                [sickDays, "Sick days"],
                [workDays, "Work days logged"],
              ].map(([val, lbl, color]) => (
                <div key={lbl} style={S.yearStat}>
                  <div style={{...S.yearStatVal, ...(color?{color}:{})}}>{val}</div>
                  <div style={S.yearStatLbl}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

// ── Export View ───────────────────────────────────────────────────────────
function ExportView({ entries, getMonthMileagePay }) {
  const [exportType, setExportType] = useState("daily");
  const year = { start:"2026-04-10", end:"2027-04-09", label:"2026-2027" };

  const buildCSV = () => {
    if (exportType === "daily") {
      const rows = [["Date","Day","Type","Hours Worked","Contracted Hours","Hours Delta","Miles","Mileage Pay (£)"]];
      const ye = Object.entries(entries).filter(([d]) => d>=year.start&&d<=year.end).sort(([a],[b]) => a.localeCompare(b));
      let cumMiles = 0;
      for (const [d, e] of ye) {
        const dayName = new Date(d+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long"});
        const type = e.type || "work";
        const countsHours = type === "work" || type === "weekend";
        const hours = countsHours && e.hours !== "" ? parseFloat(e.hours) : "";
        const miles = countsHours ? (parseFloat(e.miles) || 0) : 0;
        const contracted = type === "work" ? CONTRACT_HOURS_PER_DAY : (type === "weekend" ? 0 : "");
        const delta = hours !== "" && contracted !== "" ? (hours - contracted).toFixed(2) : "";
        let miPay = 0;
        if (miles > 0) {
          const lo = Math.min(miles, Math.max(0, MILEAGE_THRESHOLD - cumMiles));
          miPay = lo * MILEAGE_RATE_LOW + (miles - lo) * MILEAGE_RATE_HIGH;
        }
        cumMiles += miles;
        rows.push([d, dayName, type, hours, contracted, delta, miles||"", miPay>0?miPay.toFixed(2):""]);
      }
      return rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    } else {
      const rows = [["Month","Work Days","Weekend Days Worked","Total Hours","Miles","Mileage Pay (£)","Holidays","Sick Days"]];
      const ye = Object.entries(entries).filter(([d]) => d>=year.start&&d<=year.end);
      for (const m of [...new Set(ye.map(([d]) => d.slice(0,7)))].sort()) {
        const me = ye.filter(([d]) => d.startsWith(m));
        rows.push([
          formatMonth(m),
          me.filter(([,e])=>e.type==="work"&&e.hours!=="").length,
          me.filter(([,e])=>e.type==="weekend"&&e.hours!==""&&parseFloat(e.hours)>0).length,
          me.filter(([,e])=>(e.type==="work"||e.type==="weekend")&&e.hours!=="").reduce((s,[,e])=>s+parseFloat(e.hours),0).toFixed(2),
          me.filter(([,e])=>e.type==="work"||e.type==="weekend").reduce((s,[,e])=>s+(parseFloat(e.miles)||0),0).toFixed(1),
          getMonthMileagePay(m).toFixed(2),
          me.filter(([,e])=>e.type==="holiday").length,
          me.filter(([,e])=>e.type==="sick").length,
        ]);
      }
      return rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    }
  };

  const download = () => {
    const blob = new Blob([buildCSV()], {type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `hours-tracker-${year.label}-${exportType}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const previewRows = exportType === "daily"
    ? Object.entries(entries).filter(([d])=>d>=year.start&&d<=year.end).sort(([a],[b])=>a.localeCompare(b)).slice(0,8)
    : [];

  return (
    <section>
      <div style={S.sectionHeader}><h2 style={S.sectionTitle}>Export Data</h2></div>
      <div style={S.exportControls}>
        <div style={S.exportGroup}>
          <label style={S.fieldLabel}>Format</label>
          <div style={S.exportToggle}>
            <button style={{...S.exportToggleBtn,...(exportType==="daily"?S.exportToggleActive:{})}} onClick={()=>setExportType("daily")}>Daily</button>
            <button style={{...S.exportToggleBtn,...(exportType==="monthly"?S.exportToggleActive:{})}} onClick={()=>setExportType("monthly")}>Monthly</button>
          </div>
        </div>
        <button style={S.downloadBtn} onClick={download}>↓ Download CSV</button>
      </div>
      <div style={S.exportInfo}><p style={{margin:0,color:"#778",fontSize:13}}>{exportType==="daily"?"One row per working day.":"One row per month with totals."}</p></div>
      {previewRows.length > 0 && (
        <>
          <h3 style={S.logTitle}>Preview</h3>
          <div style={{overflowX:"auto"}}>
            <table style={S.previewTable}>
              <thead><tr>{["Date","Type","Hours","Miles"].map(h=><th key={h} style={S.previewTh}>{h}</th>)}</tr></thead>
              <tbody>{previewRows.map(([d,e])=><tr key={d}><td style={S.previewTd}>{formatDate(d)}</td><td style={S.previewTd}>{e.type||"work"}</td><td style={S.previewTd}>{e.hours||"—"}</td><td style={S.previewTd}>{e.miles||"—"}</td></tr>)}</tbody>
            </table>
          </div>
        </>
      )}
      {previewRows.length === 0 && exportType === "daily" && <Empty text="No data for this year yet." />}
    </section>
  );
}

function Empty({ text }) { return <div style={S.empty}>{text}</div>; }

// ══ Styles ════════════════════════════════════════════════════════════════
const S = {
  app:{minHeight:"100vh",background:"#111520",color:"#dde",fontFamily:"'DM Sans','Segoe UI',sans-serif",display:"flex",flexDirection:"column"},
  spinner:{width:32,height:32,border:"3px solid #333",borderTop:"3px solid #c9a84c",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto"},
  loginWrap:{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#111520"},
  loginCard:{background:"#161b2e",border:"1px solid #2a3050",borderRadius:16,padding:"48px 40px",maxWidth:360,width:"100%",textAlign:"center",boxShadow:"0 8px 40px #0008"},
  loginLogo:{fontSize:40,marginBottom:8,color:"#c9a84c"},
  loginTitle:{margin:"0 0 4px",fontSize:24,fontWeight:700,letterSpacing:1,color:"#eee"},
  loginSub:{margin:"0 0 28px",color:"#778",fontSize:14},
  loginInput:{width:"100%",marginBottom:12,textAlign:"center",fontSize:18,letterSpacing:4},
  loginBtn:{width:"100%",padding:"12px",background:"#c9a84c",border:"none",borderRadius:8,color:"#111",fontWeight:700,fontSize:16,cursor:"pointer",letterSpacing:1},
  errorMsg:{color:"#e05c5c",fontSize:13,margin:"8px 0 0"},
  header:{background:"#161b2e",borderBottom:"1px solid #222b45",padding:"14px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8},
  headerLeft:{display:"flex",alignItems:"center",gap:12},
  logo:{fontSize:24,color:"#c9a84c"},
  headerTitle:{fontSize:18,fontWeight:700,letterSpacing:0.5},
  headerSub:{fontSize:12,color:"#778"},
  headerRight:{display:"flex",alignItems:"center",gap:16},
  headerStat:{display:"flex",flexDirection:"column",alignItems:"center",gap:1},
  headerStatVal:{fontSize:20,fontWeight:800,lineHeight:1},
  headerStatLbl:{fontSize:10,color:"#778",textTransform:"uppercase",letterSpacing:0.8},
  headerDivider:{width:1,height:32,background:"#2a3050"},
  savingDot:{fontSize:10,color:"#c9a84c"},
  logoutBtn:{padding:"6px 14px",background:"transparent",border:"1px solid #334",borderRadius:6,color:"#778",cursor:"pointer",fontSize:13},
  nav:{background:"#13182a",borderBottom:"1px solid #1e2540",display:"flex",padding:"0 16px",overflowX:"auto"},
  navBtn:{padding:"12px 16px",background:"none",border:"none",borderBottom:"2px solid transparent",color:"#778",cursor:"pointer",fontSize:13,fontWeight:500,whiteSpace:"nowrap"},
  navBtnActive:{color:"#c9a84c",borderBottomColor:"#c9a84c"},
  main:{flex:1,padding:"24px 20px",maxWidth:900,width:"100%",margin:"0 auto",boxSizing:"border-box"},
  weekNav:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16},
  weekLabel:{display:"flex",alignItems:"center",gap:10,fontSize:15,fontWeight:600,color:"#ccd"},
  todayBtn:{padding:"3px 10px",background:"transparent",border:"1px solid #c9a84c44",borderRadius:4,color:"#c9a84c",cursor:"pointer",fontSize:12},
  iconBtn:{background:"none",border:"1px solid #2a3050",borderRadius:6,color:"#aab",cursor:"pointer",fontSize:22,width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center"},
  weekSummaryRow:{display:"flex",alignItems:"center",background:"#161b2e",border:"1px solid #2a3050",borderRadius:10,marginBottom:18,padding:"12px 0"},
  weekStat:{flex:1,textAlign:"center",display:"flex",flexDirection:"column",gap:2},
  weekStatVal:{fontSize:22,fontWeight:700,color:"#eee"},
  weekStatLbl:{fontSize:11,color:"#778",textTransform:"uppercase",letterSpacing:0.8},
  weekStatDiv:{width:1,background:"#2a3050",alignSelf:"stretch"},
  dayGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))",gap:12},
  dayCard:{border:"1.5px solid",borderRadius:10,padding:"14px 12px",display:"flex",flexDirection:"column",gap:8},
  dayCardHeader:{display:"flex",alignItems:"center",justifyContent:"space-between"},
  dayName:{fontSize:12,fontWeight:600,letterSpacing:0.3},
  bhBadge:{fontSize:10,background:"#4a3560",color:"#cba8ff",borderRadius:4,padding:"1px 5px"},
  weBadge:{fontSize:10,background:"#242a3a",color:"#89a",borderRadius:4,padding:"1px 5px"},
  typeSelect:{background:"#0e1220",border:"1px solid #2a3050",borderRadius:6,color:"#aab",padding:"4px 6px",fontSize:12,width:"100%",cursor:"pointer"},
  dayFields:{display:"flex",flexDirection:"column",gap:6},
  fieldLabel:{fontSize:11,color:"#778",marginBottom:-4},
  input:{background:"#0e1220",border:"1px solid #2a3050",borderRadius:6,color:"#eee",padding:"7px 10px",fontSize:13,outline:"none",width:"100%",boxSizing:"border-box"},
  inputError:{borderColor:"#e05c5c"},
  dayTypeLabel:{fontSize:13,color:"#778",paddingTop:4},
  sectionHeader:{marginBottom:20},
  sectionTitle:{fontSize:20,fontWeight:700,margin:"0 0 6px",color:"#eee"},
  infoChip:{fontSize:12,color:"#aab",background:"#161b2e",border:"1px solid #2a3050",borderRadius:6,padding:"4px 10px",display:"inline-block"},
  monthList:{display:"flex",flexDirection:"column",gap:8},
  monthRow:{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#161b2e",border:"1px solid #2a3050",borderRadius:10,padding:"14px 18px"},
  monthName:{fontSize:16,fontWeight:600,color:"#dde"},
  monthDetail:{fontSize:13,color:"#778",marginTop:2},
  paidToggle:{padding:"7px 16px",border:"1.5px solid",borderRadius:8,color:"#ccd",cursor:"pointer",fontWeight:600,fontSize:13},
  bankHero:{textAlign:"center",padding:"28px 0 20px",borderBottom:"1px solid #1e2540",marginBottom:20},
  bankBig:{fontSize:56,fontWeight:800,letterSpacing:-2,lineHeight:1},
  bankHeroSub:{fontSize:14,color:"#778",marginTop:6},
  adminPanel:{background:"#161b2e",border:"1px solid #2a3050",borderRadius:10,padding:18,marginBottom:22},
  adminPanelTitle:{margin:"0 0 12px",fontSize:14,fontWeight:700,color:"#c9a84c",textTransform:"uppercase",letterSpacing:1},
  adjRow:{display:"flex",gap:8,flexWrap:"wrap"},
  adjBtn:{padding:"7px 18px",background:"#c9a84c",border:"none",borderRadius:6,color:"#111",fontWeight:700,cursor:"pointer",fontSize:13},
  zeroBtn:{marginTop:12,width:"100%",padding:"10px",background:"#3a1f1f",border:"1.5px solid #5c3030",borderRadius:8,color:"#e05c5c",cursor:"pointer",fontWeight:600,fontSize:13},
  resetZone:{marginTop:16,borderTop:"1px solid #1e2540",paddingTop:14},
  backupZone:{marginTop:16,borderTop:"1px solid #1e2540",paddingTop:14},
  backupTitle:{margin:"0 0 4px",fontSize:13,fontWeight:700,color:"#8cb85c",textTransform:"uppercase",letterSpacing:0.8},
  backupHint:{margin:"0 0 10px",fontSize:12,color:"#778"},
  backupRow:{display:"flex",gap:8,flexWrap:"wrap"},
  backupBtn:{padding:"8px 14px",background:"#1e2a20",border:"1px solid #3a6040",borderRadius:6,color:"#8cb85c",cursor:"pointer",fontSize:13,fontWeight:600,flex:1},
  resetBtn:{background:"transparent",border:"1px solid #3a2a1a",borderRadius:6,color:"#664",cursor:"pointer",fontSize:12,padding:"6px 14px"},
  resetConfirm:{background:"#1e1510",border:"1px solid #5c3a1a",borderRadius:8,padding:"12px 14px",display:"flex",flexDirection:"column",gap:10},
  resetConfirmText:{fontSize:13,color:"#c97"},
  resetConfirmBtns:{display:"flex",gap:8},
  resetConfirmYes:{padding:"7px 16px",background:"#5c2a1a",border:"1px solid #8c4a2a",borderRadius:6,color:"#e08060",cursor:"pointer",fontWeight:700,fontSize:13},
  resetConfirmNo:{padding:"7px 16px",background:"transparent",border:"1px solid #334",borderRadius:6,color:"#778",cursor:"pointer",fontSize:13},
  logTitle:{fontSize:14,fontWeight:700,color:"#778",textTransform:"uppercase",letterSpacing:1,margin:"0 0 10px"},
  logList:{display:"flex",flexDirection:"column",gap:4,maxHeight:340,overflowY:"auto"},
  logRow:{display:"flex",alignItems:"center",gap:10,background:"#13182a",borderRadius:7,padding:"8px 12px",fontSize:13},
  logDate:{color:"#778",minWidth:100,fontSize:12},
  logNote:{flex:1,color:"#aab"},
  logDelta:{fontWeight:700,minWidth:56,textAlign:"right"},
  logBalance:{color:"#556",fontSize:12,minWidth:52,textAlign:"right"},
  leaveCards:{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20},
  leaveCard:{background:"#161b2e",border:"1px solid #2a3050",borderRadius:10,padding:"20px 16px",textAlign:"center"},
  leaveNum:{fontSize:40,fontWeight:800,color:"#c9a84c"},
  leaveLbl:{fontSize:13,fontWeight:600,color:"#ccd",margin:"4px 0 2px"},
  leaveOut:{fontSize:12,color:"#778"},
  barWrap:{height:8,background:"#1e2540",borderRadius:4,overflow:"hidden",marginBottom:24},
  bar:{height:"100%",borderRadius:4,transition:"width 0.5s"},
  yearCard:{background:"#161b2e",border:"1px solid #2a3050",borderRadius:12,padding:20,marginBottom:16},
  yearCardHeader:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16},
  yearCardTitle:{fontSize:15,fontWeight:700,color:"#dde"},
  currentBadge:{fontSize:11,background:"#2a3a1a",color:"#8cb85c",border:"1px solid #4a6030",borderRadius:4,padding:"2px 8px"},
  yearStats:{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(130px, 1fr))",gap:12},
  yearStat:{background:"#111520",borderRadius:8,padding:12,textAlign:"center"},
  yearStatVal:{fontSize:18,fontWeight:700,color:"#eee",marginBottom:3},
  yearStatLbl:{fontSize:11,color:"#778",lineHeight:1.3},
  exportControls:{display:"flex",alignItems:"flex-end",gap:16,flexWrap:"wrap",marginBottom:16,background:"#161b2e",border:"1px solid #2a3050",borderRadius:10,padding:18},
  exportGroup:{display:"flex",flexDirection:"column",gap:6},
  exportToggle:{display:"flex",borderRadius:6,overflow:"hidden",border:"1px solid #2a3050"},
  exportToggleBtn:{padding:"7px 16px",background:"#0e1220",border:"none",color:"#778",cursor:"pointer",fontSize:13},
  exportToggleActive:{background:"#c9a84c",color:"#111",fontWeight:700},
  downloadBtn:{padding:"9px 22px",background:"#c9a84c",border:"none",borderRadius:8,color:"#111",fontWeight:700,fontSize:14,cursor:"pointer",marginLeft:"auto"},
  exportInfo:{background:"#13182a",border:"1px solid #1e2540",borderRadius:8,padding:"12px 16px",marginBottom:20},
  previewTable:{width:"100%",borderCollapse:"collapse",fontSize:13,marginTop:4},
  previewTh:{background:"#13182a",color:"#778",padding:"8px 12px",textAlign:"left",fontSize:11,textTransform:"uppercase",letterSpacing:0.8,borderBottom:"1px solid #2a3050"},
  previewTd:{padding:"7px 12px",borderBottom:"1px solid #1a2040",color:"#ccd"},
  toast:{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",borderRadius:8,padding:"10px 20px",fontWeight:600,fontSize:14,zIndex:999,boxShadow:"0 4px 20px #0006",border:"1px solid"},
  empty:{color:"#556",textAlign:"center",padding:"40px 0",fontSize:14},
};
